import { productCardCarousels } from '~js/utils/product-card-swiper';
import { postAddToCart } from '../../utils/variant';
const productDrawerMobile = {
  section: null,
  optionInputs: null,
  optionGroups: null,
  variantSelectElement: null,
  productBuyBox: null,
  oneTimeOption: null,
  selectedOptionsString: null,
  optionsDivider: ' / ',
  addToCartButton: null,
  ifdisplayedProductClicked: false,
  /**
   * Renders the product drawer modal and initializes product data and swipers.
   * @param {HTMLElement} section - The section element where the product content will be rendered.
   * @param {string} productHTML - HTML string containing the product content to be rendered inside the drawer section.
   */
  renderDrawerModal(section, productHTML, ifdisplayedProductClicked = false) {
    this.section = section;
    if (productHTML === null) {
      return (this.section.innerHTML =
        'Product details are unavailable, please try again.');
    }
    this.ifdisplayedProductClicked = ifdisplayedProductClicked;
    this.section.innerHTML = productHTML;
    this.initializeElements();
    this.initializeColorSwatchCarousels();
    this.addEventHandlers();
    this.updateSwatchAvailability();
    this.updateSubscriptionButtonToChecked();
    this.addWindowEventListeners();
  },

  // initialize drawer elements
  initializeElements() {
    this.optionInputs = this.section.querySelectorAll(
      '.js-quick-add-product-option-group-value',
    );
    this.optionGroups = this.section.querySelectorAll(
      '.js-quick-add-product-options-group',
    );
    this.variantSelectElement = this.section.querySelector(
      '.js-quick-add-product-variant-select-element',
    );
    this.productBuyBox = this.section.querySelector(
      '.js-mobile-drawer-product-buybox',
    );
    this.oneTimeOption = this.productBuyBox?.querySelector(
      'input[value="one_time"]',
    );
    this.addToCartButton = this.section.querySelector(
      '.js-product-quick-buy-add-to-bag',
    );
    this.selectedOptionsString =
      this.variantSelectElement?.querySelector(
        'option:checked',
      )?.dataset?.title;
  },

  // initiate the product color carousel
  initializeColorSwatchCarousels() {
    const colorSwatchContainer = this.section.querySelector(
      '.js-color-swatch-swiper-container',
    );
    this.productColorSwiper = productCardCarousels(
      colorSwatchContainer,
      10,
      10,
      8,
      this.section.querySelector(
        '.js-product-drawer-color-swatch-thumbnail-next',
      ),
      this.section.querySelector(
        '.js-product-drawer-color-swatch-thumbnail-prev',
      ),
    );
  },

  // Adds event handlers to product option and the Add to Cart button.
  addEventHandlers() {
    if (this.section === null) return;
    this.optionInputs.forEach((option) => {
      option.addEventListener('change', () => {
        this.handleVariantSelection();
        this.updateSwatchAvailability();
        this.updateVariantPrices();
      });
    });
    if (!this.addToCartButton === null) return;
    if (this.selectedOptionsString != null) {
      this.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  },

  addWindowEventListeners() {
    // Listen for PDP variant changes and update drawer options accordingly
    window.addEventListener('pdpVariantChange', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (variantId) {
        this.setVariantOptionsById(variantId);
      }
    });
  },

  setVariantOptionsById(variantId) {
    // Find the matching option based on variantId
    const matchingOption = this.variantSelectElement.querySelector(
      `option[value="${variantId}"]`,
    );
    if (matchingOption) {
      this.variantSelectElement.value = variantId;
      this.selectedOptionsString = matchingOption.dataset.title;
      this.updateSelectedOptionClasses(this.selectedOptionsString);
      if (this.productBuyBox) this.updateVariantSellingPlan(matchingOption);
    } else {
      const selectedOptionsString =
        this.variantSelectElement.querySelector('option:checked')?.dataset
          ?.title;
      this.updateSelectedOptionClasses(selectedOptionsString);
    }
  },

  // Method to update selected options based on a given selectedOptionsString
  updateSelectedOptionClasses(selectedOptionsString) {
    const selectedOptions = selectedOptionsString.split(this.optionsDivider);

    this.optionGroups.forEach((optionGroup, index) => {
      const options = optionGroup.querySelectorAll(
        '.js-quick-add-product-option-group-value',
      );
      options.forEach((option) => {
        if (option.value === selectedOptions[index]) {
          option
            .closest('.js-quick-add-product-options-group')
            .querySelector('.js-quick-add-product-option-value').innerHTML =
            option.value;
          option.removeAttribute('disabled');
          option.checked = true;
          this.updateSwatchAvailability();
        }
      });
    });
  },

  updateVariantPrices() {
    const price = this.variantSelectElement
      ?.querySelector('option:checked')
      ?.getAttribute('price');
    const compareAtPrice = this.variantSelectElement
      ?.querySelector('option:checked')
      ?.getAttribute('compare_at_price');

    const priceElement = this.section.querySelectorAll(
      '.js-quick-add-product-variant-price',
    );
    const compareAtPriceElement = this.section.querySelectorAll(
      '.js-quick-add-product-variant-compare_at_price',
    );
    compareAtPriceElement.forEach((element) => {
      element.innerHTML = compareAtPrice;
    });
    priceElement.forEach((element) => {
      element.innerHTML = price;
    });
  },

  // update the options for the selected options
  updateSwatchAvailability() {
    this.optionGroups.forEach((group, groupIndex) => {
      const options = group.querySelectorAll(
        '.js-quick-add-product-option-group-value',
      );
      options.forEach((input) => {
        const selectedOptions = this.selectedOptionsString.split(
          this.optionsDivider,
        );
        selectedOptions[groupIndex] = input.value;
        const variantSelector = selectedOptions.join(this.optionsDivider);
        const variantSelectOption = this.variantSelectElement.querySelector(
          `[data-title="${variantSelector}"]`,
        );
        input.toggleAttribute('disabled', variantSelectOption.disabled);
      });
    });
  },

  handleVariantSelection() {
    const selectedOptions = [];
    this.optionGroups.forEach((optionGroup) => {
      const selectedOption = optionGroup.querySelector('input:checked');
      selectedOptions.push(selectedOption.value);
      this.updateSelectedOptionLabel(optionGroup, selectedOption.value);
    });
    const selectedOptionsString = selectedOptions.join(this.optionsDivider);
    this.selectedOptionsString = selectedOptionsString;
    const matchingOption = this.variantSelectElement.querySelector(
      `[data-title="${selectedOptionsString}"]`,
    );
    this.variantSelectElement.value = matchingOption.value;
    this.variantSelectElement.dispatchEvent(new Event('change'));

    if (this.productBuyBox) this.updateVariantSellingPlan(matchingOption);

    // Dispatch custom event with selected variant ID to update the main pdp varaintID
    if (!this.ifdisplayedProductClicked) {
      return;
    }
    const variantId = this.variantSelectElement?.value;
    let variant = null;
    if (
      this.variantSelectElement.querySelector(`option[value="${variantId}"]`)
        ?.selected
    ) {
      variant = this.variantSelectElement.querySelector(
        `option[value="${variantId}"]`,
      );
    }

    window.dispatchEvent(
      new CustomEvent('variantChange', { detail: { variant } }),
    );
  },

  updateVariantSellingPlan(variant) {
    const hasSellingPlans =
      variant.dataset?.hasSellingPlans === 'true' ? true : false;

    // Toggle Buy Box based on variant selling plan
    this.productBuyBox.classList.toggle('hidden', !hasSellingPlans);
    if (!hasSellingPlans && this.oneTimeOption && !this.oneTimeOption.checked) {
      this.oneTimeOption.click();
    }
  },

  // update selected option name
  updateSelectedOptionLabel(group, value) {
    group.querySelector('.js-quick-add-product-option-value').innerHTML = value;
  },

  // Helper function to toggle button state
  toggleAddToCartButtonState(isAddingProduct) {
    this.addToCartButton.classList.toggle('is-adding', isAddingProduct);
    const quickBuyText = this.addToCartButton.querySelector(
      '.quick-buy-cta-text',
    );
    const quickBuyLoader = this.addToCartButton.querySelector(
      '.quick-buy-cta-loader',
    );
    if (quickBuyText) {
      quickBuyText.classList.toggle('hidden', isAddingProduct);
    }

    if (quickBuyLoader) {
      quickBuyLoader.classList.toggle('hidden', !isAddingProduct);
    }
  },

  // to mark the subscription option checked if an option is selected from the subscription options dropdown
  updateSubscriptionButtonToChecked() {
    const section = this.section;
    const frequencySelects = section.querySelectorAll(
      'select[name="frequency"]',
    );
    frequencySelects.forEach((selectElement, index) => {
      selectElement.addEventListener('change', function () {
        const subscriptionRadio = section.querySelector(
          `.subscription-radio-${index + 1}`,
        );
        subscriptionRadio.checked = true;
      });
    });
  },

  // Adds a selected product variant to the cart with an optional subscription frequency.
  async addToCart() {
    const variandId = this.variantSelectElement.value;
    if (!variandId) return;
    this.toggleAddToCartButtonState(true);
    let selectedFrequency = null;
    const selectedOptionElement = this.section.querySelector(
      'input.js-purchase-option:checked',
    );
    if (selectedOptionElement != null) {
      const selectedOptionValue = selectedOptionElement.value;
      if (selectedOptionValue !== 'one_time') {
        const closestOption = selectedOptionElement.closest(
          '.js-subscription-options__option',
        );
        selectedFrequency = closestOption.querySelector(
          'select[name="frequency"]',
        ).value;
      }
    }
    const response = await postAddToCart(variandId, selectedFrequency);
    if (!response) return;
    this.toggleAddToCartButtonState(false);
    window.QuickBuyMobile.hide();
  },
};

export const renderDrawerModalMobile =
  productDrawerMobile.renderDrawerModal.bind(productDrawerMobile);
